import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import * as UpChunk from '@mux/upchunk';
import { VideoProvider } from '../../apis/api-client';
import { Box, Button, LinearProgress, SelectField, TextField, Typography } from '../ui';

/**
 * Owner-agnostic video source editor. Used by EditLesson (lesson video) and
 * the EditCourse "Detail Page" section (course detail video). The component
 * owns all upload state; the parent only supplies a callback that returns a
 * fresh Mux direct-upload URL — that callback is the only thing that differs
 * between owners (lessons hit /api/storage/generate-video-upload, course
 * detail hits /api/courses/{id}/detail-video-upload).
 *
 * Flow for create-new entities (lesson/course doesn't exist yet):
 *   1. User picks provider, optionally selects a video file.
 *   2. Parent's "Save" handler creates the entity, gets an id back.
 *   3. Parent calls `ref.current.startUploadIfPending()` — component fires
 *      `getMuxUploadUrl(id)` and starts UpChunk.
 *
 * Flow for existing entities:
 *   - Same, but the entity already has an id, so step 2 is a no-op.
 */

export interface VideoSourceEditorHandle {
  /**
   * Kick off the Mux upload if a file is staged and provider is Mux. Resolves
   * to true on upload completion, false if there was no pending file or the
   * upload failed. Parent typically awaits this in its save handler.
   */
  startUploadIfPending: () => Promise<boolean>;
  /**
   * Whether a file is currently staged for upload (used by parents that need
   * to know whether to keep the dialog open while the upload runs).
   */
  hasPendingUpload: () => boolean;
  /**
   * Convenience: returns a validation error string for the configured source,
   * or null if valid. Parents call this from their own validate() so the rules
   * stay co-located with the field.
   */
  validate: () => string | null;
}

interface ExistingVideoSnapshot {
  muxUploadId?: string;
  muxPlaybackId?: string;
  videoStatus?: string;
  videoDuration?: number;
}

interface VideoSourceEditorProps {
  // Current source selection (controlled by parent so its form state is the
  // single source of truth for non-upload fields).
  provider: VideoProvider;
  onProviderChange: (provider: VideoProvider) => void;
  externalVideoId: string;
  onExternalVideoIdChange: (value: string) => void;
  externalVideoIdError?: string;

  // Snapshot of the entity's current Mux fields, used to render the "video
  // already uploaded" banner. Pass undefined when creating a new entity.
  existing?: ExistingVideoSnapshot;

  // Required for Mux flow: returns a fresh upload URL. Component calls this
  // when it's time to start UpChunk.
  getMuxUploadUrl: () => Promise<string>;

  // Lifecycle hooks
  onUploadComplete?: () => void;
  onUploadError?: (message: string) => void;

  // Whether to require a video file when creating new (Mux only). Parent
  // typically passes `!existingEntity` so we don't force a re-upload on edit.
  requireFileOnCreate?: boolean;
}

interface VideoFileInfo {
  name: string;
  size: number;
  sizeFormatted: string;
  type: string;
}

const VideoSourceEditor = forwardRef<VideoSourceEditorHandle, VideoSourceEditorProps>(function VideoSourceEditor(
  {
    provider,
    onProviderChange,
    externalVideoId,
    onExternalVideoIdChange,
    externalVideoIdError,
    existing,
    getMuxUploadUrl,
    onUploadComplete,
    onUploadError,
    requireFileOnCreate = false,
  },
  ref
) {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoFileInfo, setVideoFileInfo] = useState<VideoFileInfo | null>(null);
  const [videoValidationError, setVideoValidationError] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const uploadInstanceRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    startUploadIfPending: async () => {
      if (provider !== VideoProvider.Mux) return true;
      if (!videoFile) return true;

      try {
        const uploadUrl = await getMuxUploadUrl();
        return await new Promise<boolean>((resolve) => {
          setVideoUploading(true);
          setUploadStartTime(Date.now());
          setUploadProgress(0);
          setUploadedBytes(0);

          const upload = UpChunk.createUpload({
            endpoint: uploadUrl,
            file: videoFile,
            chunkSize: 30720, // 30 MB chunks — matches existing lesson upload
          });

          upload.on('progress', (e: any) => {
            const pct = Math.round(e.detail);
            setUploadProgress(pct);
            if (videoFileInfo?.size) {
              setUploadedBytes((pct / 100) * videoFileInfo.size);
            }
          });
          upload.on('success', () => {
            setVideoUploading(false);
            uploadInstanceRef.current = null;
            onUploadComplete?.();
            resolve(true);
          });
          upload.on('error', (err: any) => {
            setVideoUploading(false);
            uploadInstanceRef.current = null;
            const msg = err?.detail?.message || 'Upload failed';
            onUploadError?.(msg);
            resolve(false);
          });
          uploadInstanceRef.current = upload;
        });
      } catch (e: any) {
        setVideoUploading(false);
        onUploadError?.(e?.message || 'Failed to start upload');
        return false;
      }
    },
    hasPendingUpload: () => provider === VideoProvider.Mux && !!videoFile,
    validate: () => {
      if (provider === VideoProvider.Wistia) {
        if (!externalVideoId.trim()) return 'Wistia media ID is required';
        return null;
      }
      // Mux
      if (requireFileOnCreate && !videoFile && !existing?.muxUploadId) {
        return 'Please select a video file';
      }
      return null;
    },
  }));

  const acceptFile = (file: File) => {
    setVideoFile(file);
    setVideoFileInfo({
      name: file.name,
      size: file.size,
      sizeFormatted: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      type: file.type,
    });
    setVideoValidationError('');
  };

  const cancelUpload = () => {
    if (uploadInstanceRef.current) {
      uploadInstanceRef.current.abort();
      uploadInstanceRef.current = null;
    }
    setVideoUploading(false);
    setVideoFile(null);
    setVideoFileInfo(null);
    setUploadProgress(0);
    setUploadStartTime(null);
    setUploadedBytes(0);
  };

  const getUploadMetrics = () => {
    if (!uploadStartTime || !videoFileInfo?.size || uploadProgress === 0) {
      return { speed: 0, timeRemaining: 0 };
    }
    const elapsedSeconds = (Date.now() - uploadStartTime) / 1000;
    const speed = uploadedBytes / elapsedSeconds; // bytes/sec
    const remainingBytes = videoFileInfo.size - uploadedBytes;
    return {
      speed: speed / (1024 * 1024),
      timeRemaining: Math.ceil(remainingBytes / speed),
    };
  };

  return (
    <Box sx={{ mb: 2 }}>
      <SelectField
        label="Video Source"
        value={provider}
        onChange={(e) => onProviderChange(Number(e.target.value) as VideoProvider)}
        options={[
          { label: 'Mux (upload video file)', value: VideoProvider.Mux },
          { label: 'Wistia (external video ID)', value: VideoProvider.Wistia },
        ]}
        allowEmpty={false}
        fullWidth
        sx={{ mb: 2 }}
      />

      {provider === VideoProvider.Wistia && (
        <Box>
          <TextField
            label="Wistia Media ID"
            value={externalVideoId}
            onChange={(e) => onExternalVideoIdChange(e.target.value)}
            fullWidth
            placeholder="e.g. abc123def4"
            error={!!externalVideoIdError}
            helperText={externalVideoIdError || 'Find this in your Wistia media URL (the 10-character ID).'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The video will be embedded directly from Wistia — no upload needed.
          </Typography>
        </Box>
      )}

      {provider === VideoProvider.Mux && (
        <>
          {existing?.muxUploadId && !videoFile && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 1,
                bgcolor: 'success.50',
              }}
            >
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                ✓ Video already uploaded
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {existing.muxPlaybackId
                  ? `Video is ready for playback${existing.videoDuration ? ` (${Math.floor(existing.videoDuration / 60)}:${String(Math.floor(existing.videoDuration % 60)).padStart(2, '0')})` : ''}`
                  : 'Video is being processed. Please check back in a few minutes.'}
              </Typography>
              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1 }}>
                Drag & drop below to upload a replacement video (optional)
              </Typography>
            </Box>
          )}

          {!videoUploading && (
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept="video/*"
                id="video-source-editor-input"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) acceptFile(file);
                }}
              />

              {!videoFile ? (
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: videoValidationError ? 'error.main' : 'grey.400',
                    borderRadius: 1,
                    bgcolor: videoValidationError ? 'error.50' : 'grey.50',
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                  onClick={() => document.getElementById('video-source-editor-input')?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('video/')) {
                      acceptFile(file);
                    } else {
                      setVideoValidationError('Please select a valid video file');
                    }
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    📹 Drag and drop video file here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to browse
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: 1,
                    bgcolor: 'success.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="success.dark">
                      ✓ Video file selected: <strong>{videoFileInfo?.name}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Size: {videoFileInfo?.sizeFormatted} | Type: {videoFileInfo?.type}
                    </Typography>
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1 }}>
                      Click "Save" below to start the upload
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setVideoFile(null);
                      setVideoFileInfo(null);
                    }}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    ✕
                  </Button>
                </Box>
              )}

              {videoValidationError && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                  {videoValidationError}
                </Typography>
              )}
            </Box>
          )}

          {videoUploading && (
            <Box
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'primary.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                  📹 Uploading video: {videoFileInfo?.name}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  mb: 1.5,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    background:
                      'var(--brand-gradient)',
                  },
                }}
              />

              <Typography variant="h6" color="primary.main" align="center" sx={{ mb: 2, fontWeight: 700 }}>
                {uploadProgress}%
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {(uploadedBytes / (1024 * 1024)).toFixed(2)} MB of {videoFileInfo?.sizeFormatted}
                </Typography>
                {uploadProgress > 5 &&
                  (() => {
                    const m = getUploadMetrics();
                    return (
                      <Typography variant="caption" color="text.secondary">
                        {m.speed.toFixed(1)} MB/s • ~
                        {m.timeRemaining < 60 ? `${m.timeRemaining}s` : `${Math.ceil(m.timeRemaining / 60)}m`} remaining
                      </Typography>
                    );
                  })()}
              </Box>

              <Button size="small" variant="outlined" color="inherit" onClick={cancelUpload} sx={{ width: '100%' }}>
                Cancel Upload
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

export default VideoSourceEditor;
